import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Avatar, Text } from '@bufferapp/ui';
import { Bell, Clock, Star, Warning, Thread } from '@bufferapp/ui/Icon';
import { grayDark, white } from '@bufferapp/ui/style/colors';
import DraftsIcon from '@bufferapp/ui/Icon/Icons/Drafts';
import CopyIcon from '@bufferapp/ui/Icon/Icons/Copy';
import dayjs from 'dayjs';

import { usePostPermissions } from '~/post/hooks';
import { transform24hrTimeToString } from '../../../composer/composer/utils/TimeStringTransformer';
import PendingTiktokBanner from './PendingTiktokBanner';
import MediaThumbnail from '../MediaThumbnail';
import RetweetItem from './RetweetItem';
import MetadataList from './MetadataList';
import CampaignBanner from './CampaignBanner';
import {
  AttachmentCard,
  BellIconWrapper,
  ThreadIconWrapper,
  ChannelDescriptionWrapper,
  ChannelName,
  Content,
  DraftWrapper,
  DraftWrapperText,
  ErrorWrapper,
  FlexWrapper,
  MediaWrapper,
  ParagraphErrorText,
  PostLink,
  PostPreviewHeader,
  PostPreviewWrapper,
  PostText,
  PostTitle,
  TextWrapper,
  BlueprintWrapper,
  ThreadCounter,
  IconWrapper,
  ChannelSubtitle,
  StyledLink,
} from './styles';
import { PostEntity } from '../../../post/PostEntity';
import { Footer } from './Footer';

const getAttachment = ({ attachments, type }) => {
  if (!attachments) return null;
  return attachments.find((attachment) => attachment.__typename === type);
};

const getRetweet = (attachments) =>
  getAttachment({ attachments, type: 'PublishingPostRetweet' });

const getFirstComment = (attachments) =>
  getAttachment({ attachments, type: 'PublishingPostFirstComment' });

const getDestinationUrl = (attachments) =>
  getAttachment({ attachments, type: 'PublishingPostDestinationUrl' });

const getLink = (attachments) =>
  getAttachment({ attachments, type: 'PublishingPostLink' });

const PostPreview = ({
  post,
  small,
  inLastTwoRows,
  isEndOfWeek,
  isLastThirdDayOfWeek,
  setOpenModal,
}) => {
  const {
    media,
    text,
    status,
    campaign,
    error,
    channel,
    isReminder,
    isCustomScheduled,
    isPinned,
    isRetweet,
    formattedTime,
    geoLocation,
    shopgridLink,
    title,
    statistics,
    attachments,
    subprofile,
    channelData,
    isBlueprint,
    updateType,
    threadCount,
  } = post || {};
  const postMedia = media || [];
  const hasMedia = postMedia.length > 0;
  const hasError = status === 'error';
  const isSent = PostEntity.isSent(post);
  const isDraft = PostEntity.isDraft(post);
  const isPendingTiktok = PostEntity.isPendingTiktok(post);
  const needsApproval = PostEntity.needsApproval(post);
  const retweet = isRetweet ? getRetweet(attachments) : null;
  const commentText = getFirstComment(attachments)?.text ?? null;
  const destinationUrl = getDestinationUrl(attachments)?.url ?? null;
  const linkAttachment = getLink(attachments) ?? null;
  const hasThread = updateType === 'thread' && threadCount > 1;

  const {
    title: googleBusinessPostTitle,
    start_date: startDate,
    end_date: endDate,
    start_time: startTime,
    end_time: endTime,
    code,
    link,
    terms,
  } = { ...channelData?.googlebusiness };

  const { title: youtubePostTitle } = { ...channelData?.youtube };

  const { link: spLink } = { ...channelData?.startPage };
  const hasMetadata =
    commentText ||
    geoLocation ||
    shopgridLink ||
    title ||
    destinationUrl ||
    startDate ||
    endDate ||
    startTime ||
    endTime ||
    code ||
    link ||
    terms ||
    spLink;

  const getStartEndDate = () => {
    if (updateType !== 'offer' && startTime && endTime) {
      return `${dayjs.unix(startDate).utc().format('MMM Do, YYYY')} 
      ${transform24hrTimeToString(startTime)} to 
      ${dayjs.unix(endDate).utc().format('MMM Do, YYYY')} 
      ${transform24hrTimeToString(endTime)}`;
    }

    if (startDate && endDate) {
      return `${dayjs.unix(startDate).utc().format('MMM Do, YYYY')} 
      to ${dayjs.unix(endDate).utc().format('MMM Do, YYYY')}`;
    }

    return null;
  };

  const { isManager, isPostOwner } = usePostPermissions(post);
  const draftHeaderText = useMemo(() => {
    if (isBlueprint) return 'Recipe';
    if (!needsApproval) return 'Draft';
    if (isManager) return 'Awaiting Approval';
    if (!isManager && isPostOwner) return 'Pending Approval';
  }, [needsApproval, isManager, isPostOwner, isBlueprint]);

  const shouldShowLinkInError = error && error.link && error.actionType;

  return (
    <PostPreviewWrapper
      small={small}
      inLastTwoRows={inLastTwoRows}
      isEndOfWeek={isEndOfWeek}
      isLastThirdDayOfWeek={isLastThirdDayOfWeek}
    >
      {isDraft && !isBlueprint && (
        <DraftWrapper needsApproval={needsApproval}>
          <DraftsIcon size="medium" />
          <DraftWrapperText>{draftHeaderText}</DraftWrapperText>
        </DraftWrapper>
      )}
      {isDraft && isBlueprint && (
        <BlueprintWrapper needsApproval={needsApproval}>
          <CopyIcon size="medium" />
          <DraftWrapperText>{draftHeaderText}</DraftWrapperText>
        </BlueprintWrapper>
      )}
      {/* Error Banner */}
      {hasError && (
        <ErrorWrapper>
          <Warning color={white} />
          <ParagraphErrorText type="p" color="white">
            {error.message}
          </ParagraphErrorText>
          {shouldShowLinkInError && (
            <StyledLink newTab href={error.link}>
              {error.actionType}
            </StyledLink>
          )}
        </ErrorWrapper>
      )}
      {campaign && <CampaignBanner campaign={campaign} />}
      {isPendingTiktok && <PendingTiktokBanner />}
      {/* Header */}
      <PostPreviewHeader>
        <FlexWrapper>
          <Avatar
            src={channel.avatar}
            fallbackUrl="https://s3.amazonaws.com/buffer-ui/Default+Avatar.png"
            alt={channel.username}
            size="small"
            type="social"
            network={channel.service}
          />
          <ChannelDescriptionWrapper>
            <ChannelName
              type="label"
              color="grayDarker"
              title={channel.username}
            >
              {channel.username}
            </ChannelName>
            {channel.serverUrl && (
              <ChannelSubtitle
                type="label"
                color="grayDark"
                title={channel.serverUrl}
              >
                {channel.serverUrl}
              </ChannelSubtitle>
            )}
            {channel.locationData?.location && (
              <ChannelSubtitle
                type="label"
                color="grayDark"
                title={channel.locationData.location}
              >
                {channel.locationData.location}
              </ChannelSubtitle>
            )}
          </ChannelDescriptionWrapper>
        </FlexWrapper>
        <FlexWrapper>
          {hasThread && (
            <ThreadIconWrapper small>
              <Thread
                color={grayDark}
                size="small"
                role="img"
                title="This is a Thread post"
              />
              <ThreadCounter>{threadCount}</ThreadCounter>
            </ThreadIconWrapper>
          )}
          {isReminder && (
            <BellIconWrapper small>
              <Bell
                color={white}
                size="small"
                role="img"
                title="You'll receive a reminder in your Buffer app"
              />
            </BellIconWrapper>
          )}
          {isCustomScheduled && (
            <IconWrapper>
              <Clock
                color={grayDark}
                role="img"
                title="This post is a custom schedule post"
              />
            </IconWrapper>
          )}
          {isPinned && (
            <IconWrapper>
              <Star
                color={grayDark}
                role="img"
                title="This post is a pinned post"
              />
            </IconWrapper>
          )}
          <Text type="label" as="span" color="grayDark">
            {formattedTime}
          </Text>
        </FlexWrapper>
      </PostPreviewHeader>
      {/* Content */}
      <Content>
        <MediaWrapper>
          <TextWrapper>
            <PostTitle type="p">
              {title || googleBusinessPostTitle || youtubePostTitle}
            </PostTitle>
            <PostText type="p">{text}</PostText>
          </TextWrapper>
          {hasMedia && (
            <MediaThumbnail
              media={media}
              size="large"
              isReels={updateType === 'reels' ?? false}
            />
          )}
        </MediaWrapper>
        {linkAttachment && (
          <AttachmentCard>
            <MediaWrapper>
              <TextWrapper>
                <PostTitle type="p">{linkAttachment?.title}</PostTitle>
                <PostLink type="p" color="grayDark">
                  {linkAttachment?.url}
                </PostLink>
                <PostText type="p" color="grayDark">
                  {linkAttachment?.description}
                </PostText>
              </TextWrapper>
              {linkAttachment?.thumbnail && (
                <MediaThumbnail
                  media={[{ thumbnail: linkAttachment?.thumbnail }] || null}
                  size="medium"
                />
              )}
            </MediaWrapper>
          </AttachmentCard>
        )}
        {isRetweet && <RetweetItem retweet={retweet} />}
      </Content>
      {hasMetadata && (
        <MetadataList
          commentText={commentText}
          shopgridLink={shopgridLink}
          geoLocationText={geoLocation?.text}
          destinationUrl={destinationUrl}
          boardName={subprofile?.name}
          startEndDate={getStartEndDate()}
          code={code}
          link={link}
          terms={terms}
          spLink={spLink}
        />
      )}
      <Footer
        post={post}
        isSent={isSent}
        isRetweet={isRetweet}
        statistics={statistics}
        channelName={channel.service}
        setOpenModal={setOpenModal}
      />
    </PostPreviewWrapper>
  );
};

PostPreview.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.string,
    campaign: PropTypes.shape({
      color: PropTypes.shape({
        value: PropTypes.string,
      }),
      name: PropTypes.string,
    }),
  }),
  small: PropTypes.bool,
  inLastTwoRows: PropTypes.bool,
  isEndOfWeek: PropTypes.bool,
  isLastThirdDayOfWeek: PropTypes.bool,
  setOpenModal: PropTypes.func,
};

PostPreview.defaultProps = {
  post: {},
  small: false,
  inLastTwoRows: false,
  isEndOfWeek: false,
  isLastThirdDayOfWeek: false,
  setOpenModal: null,
};

export default PostPreview;
